import React, { useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Plus, Trash2, CheckCircle2, GripVertical, Zap, ListTodo, ArrowUpDown, ChevronsDown, ChevronsUp, Archive } from 'lucide-react';
import { toast } from 'sonner';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable
} from '@dnd-kit/core';
import { createContainerCollisionDetection } from '../../lib/dndCollision';

// Prefer whole columns and quadrants over the individual cards inside them, so
// a drop into a dense column lands where the pointer is. Shared with the
// artifact shelf, which passes its own category ids.
const customCollisionDetection = createContainerCollisionDetection(
    ['todo', 'in-progress', 'done', 'q1', 'q2', 'q3', 'q4']
);
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SmartInput from '../../components/SmartInput';
import SmartText from '../../components/SmartText';
import { useProjectContext } from '../../context/ProjectContext';
import { useCreateTask, useUpdateTask, useDeleteTask } from '../../hooks/useProjectQueries';
import { TasksSkeleton } from '../../components/skeletons/PageSkeletons';
import { PRIORITY_META, IMPORTANCE_META, DIFFICULTY_META, isDemoted } from '../../components/tasks/taskMeta';
import TaskControlBar, { EMPTY_FILTERS, filterTasks } from '../../components/tasks/TaskControlBar';
import { DueBadge as SharedDueBadge } from '../../components/tasks/DueBadge';
import { formatElapsed } from '../../components/tasks/taskDates';
import DemoteTasksDialog from '../../components/tasks/DemoteTasksDialog';
import { buildNewTask, withResolvedDueDate } from '../../components/tasks/buildTask';
import { Surface } from '../../components/layout/Surface';
import { cn } from '../../lib/utils';

export default function ProjectTasks() {
    const { projectId } = useParams();
    const { tasks, isLoadingTasks, readOnly } = useProjectContext();
    const [activeId, setActiveId] = useState(null);
    const [activeView, setActiveView] = useState('kanban');

    // Session-local on purpose: a filter that survives a reload is a filter you
    // forgot you set, and the board would look empty for no reason. The chips in
    // the control bar are the in-session version of the same guarantee.
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    // Mutation hooks with optimistic updates
    const createTaskMutation = useCreateTask(projectId);
    const updateTaskMutation = useUpdateTask(projectId);
    const deleteTaskMutation = useDeleteTask(projectId);

    // "ship the paywall friday" becomes a dated task here, and identically in
    // Home's quick-add and the chat panel — see buildNewTask.
    const createTask = async (title, options = {}) => {
        const task = buildNewTask(title, options);
        if (!task) return;

        try {
            await createTaskMutation.mutateAsync(task);
            toast.success("Task created");
        } catch (error) {
            toast.error("Failed to create task");
        }
    };

    const updateTask = async (id, updates) => {
        try {
            await updateTaskMutation.mutateAsync({ id, updates: withResolvedDueDate(updates) });
        } catch (error) {
            // Error handling done in mutation hook
        }
    };

    const deleteTask = async (id) => {
        try {
            await deleteTaskMutation.mutateAsync(id);
        } catch (error) {
            // Error handling done in mutation hook
        }
    };

    const toggleDone = (task) => {
        const newStatus = task.status === 'done' ? 'todo' : 'done';
        updateTask(task.id, { status: newStatus });
    };

    // Sent as a boolean, never as a timestamp: the server owns the clock and
    // stamps `demoted_at` from this. `status` is deliberately untouched, which
    // is what makes a demoted In Progress task promote back to In Progress
    // instead of being dumped on To Do.
    const setDemoted = (id, demoted) => updateTask(id, { demoted });

    // DnD
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor)
    );

    // Split once, here, rather than adding `&& !isDemoted(t)` to the eight
    // filters below. Three copies of a visibility rule is how the board and the
    // matrix end up disagreeing about what you parked.
    const [activeTasks, backlogTasks] = useMemo(() => {
        const active = [];
        const backlog = [];
        for (const task of tasks ?? []) (isDemoted(task) ? backlog : active).push(task);
        return [active, backlog];
    }, [tasks]);

    // The Backlog tab is a different pool, not a different lens — but it goes
    // through the same search and the same chips, because a backlog you cannot
    // search is a backlog you stop opening.
    const pool = activeView === 'backlog' ? backlogTasks : activeTasks;

    // One predicate for every view, so a task hidden on the board is hidden
    // in the matrix and the list too.
    const visibleTasks = useMemo(
        () => filterTasks(pool, query, filters),
        [pool, query, filters]
    );

    // Every hook is above this line. The skeleton branch used to sit higher up,
    // above useSensors — so the render that resolved the query went from three
    // hooks to five and React threw "rendered more hooks than during the
    // previous render". Early returns go last.
    if (isLoadingTasks) {
        return <TasksSkeleton />;
    }

    const handleDragStart = (event) => {
        if (readOnly) return;
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over) return;

        const taskId = active.id;
        const task = tasks.find(t => t.id === taskId);
        const containerId = over.id;

        if (['todo', 'in-progress', 'done'].includes(containerId) && task.status !== containerId) {
            updateTask(taskId, { status: containerId });
        }
        if (['q1', 'q2', 'q3', 'q4'].includes(containerId) && task.quadrant !== containerId) {
            // Map quadrant to priority/importance for consistency
            const quadrantMap = {
                q1: { priority: 'high', importance: 'high' },
                q2: { priority: 'low', importance: 'high' },
                q3: { priority: 'high', importance: 'low' },
                q4: { priority: 'low', importance: 'low' }
            };
            updateTask(taskId, { quadrant: containerId, ...quadrantMap[containerId] });
        }
    };

    const activeTask = tasks.find(t => t.id === activeId);

    // Bulk actions act on exactly `visibleTasks` — the same array the board is
    // rendering — so "12 selected" always means the twelve cards on screen.
    // They're only reachable from the active-filter row, so there is no way to
    // fire one against an unfiltered board.
    const bulkSetStatus = async (status) => {
        const targets = visibleTasks.filter(t => t.status !== status);
        if (!targets.length) {
            toast.info('Nothing to move — they are all there already');
            return;
        }
        const results = await Promise.allSettled(
            targets.map(t => updateTaskMutation.mutateAsync({ id: t.id, updates: { status } }))
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed) toast.error(`${targets.length - failed} moved, ${failed} failed`);
        else toast.success(`${targets.length} task${targets.length === 1 ? '' : 's'} moved`);
    };

    // The move the whole feature is for: filter to P:Low + I:Low, clear the
    // board of all of it at once. No confirm, unlike bulkDelete — it is one
    // click to undo, and the change log has every row.
    //
    // Shared by the bulk-action menu and the backlog's "Add existing" picker so
    // both report partial failure the same way, rather than one of them
    // resolving a bare Promise.all and silently swallowing three rejections.
    const setDemotedMany = async (ids, demoted) => {
        if (!ids.length) return;
        const results = await Promise.allSettled(
            ids.map(id => updateTaskMutation.mutateAsync({ id, updates: { demoted } }))
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        const verb = demoted ? 'demoted' : 'promoted';
        if (failed) toast.error(`${ids.length - failed} ${verb}, ${failed} failed`);
        else toast.success(`${ids.length} task${ids.length === 1 ? '' : 's'} ${verb}`);
    };

    // Completing a parked task promotes it too. Without this it would be done
    // AND demoted — finished work that appears in no Done column anywhere, only
    // inside the tab you opened precisely to stop looking at things. Unticking
    // is not symmetric: that just reopens it, still parked. The row's tooltip
    // says both halves out loud, because a bare circle cannot.
    const completeFromBacklog = (task) => {
        if (task.status === 'done') return updateTask(task.id, { status: 'todo' });
        return updateTask(task.id, { status: 'done', demoted: false });
    };

    const bulkDelete = async () => {
        const targets = visibleTasks;
        if (!targets.length) return;
        const results = await Promise.allSettled(
            targets.map(t => deleteTaskMutation.mutateAsync(t.id))
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed) toast.error(`${targets.length - failed} deleted, ${failed} failed`);
        else toast.success(`${targets.length} task${targets.length === 1 ? '' : 's'} deleted`);
    };

    return (
        <DndContext sensors={sensors} collisionDetection={customCollisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            {/* A bare flex axis — no chrome of its own. Tasks is the one route
                that stacks its surfaces vertically (bar over board) while <main>
                runs horizontally, so it needs a container to turn the axis. It
                reuses the shell's md:gap-3 so nested gaps stay on one rhythm,
                and carries no background, which is what keeps this from being
                the "one big card" the whole layout moved away from. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col md:gap-3">
                <TaskControlBar
                    activeView={activeView}
                    onViewChange={setActiveView}
                    query={query}
                    onQueryChange={setQuery}
                    filters={filters}
                    onFiltersChange={setFilters}
                    shown={visibleTasks.length}
                    total={pool.length}
                    readOnly={readOnly}
                    onBulkStatus={bulkSetStatus}
                    onBulkDelete={bulkDelete}
                    onBulkDemote={() => setDemotedMany(visibleTasks.map(t => t.id), true)}
                    onBulkPromote={() => setDemotedMany(visibleTasks.map(t => t.id), false)}
                />

                <div key={activeView} className="flex min-h-0 flex-1 flex-col animate-in fade-in duration-200">
                    {activeView === 'kanban' && (
                        <div className="flex min-h-0 flex-1 snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-4 no-scrollbar touch-pan-x sm:grid sm:grid-cols-3 sm:overflow-x-visible md:px-0 md:pb-0">
                            <div className="w-[85vw] max-w-[85vw] sm:w-auto sm:max-w-none sm:min-w-0 h-full min-h-0 snap-center flex-shrink-0 flex flex-col">
                                <KanbanColumn
                                    id="todo"
                                    title="To Do"
                                    icon={ListTodo}
                                    tasks={visibleTasks.filter(t => t.status === 'todo')}
                                    onCreateTask={(title) => createTask(title, { status: 'todo' })}
                                    onToggle={toggleDone}
                                    onDelete={deleteTask}
                                    onUpdate={updateTask}
                                    onDemote={(id) => setDemoted(id, true)}
                                    isDragging={!!activeId}
                                />
                            </div>
                            <div className="w-[85vw] max-w-[85vw] sm:w-auto sm:max-w-none sm:min-w-0 h-full min-h-0 snap-center flex-shrink-0 flex flex-col">
                                <KanbanColumn
                                    id="in-progress"
                                    title="In Progress"
                                    icon={Zap}
                                    accentColor="accent"
                                    tasks={visibleTasks.filter(t => t.status === 'in-progress')}
                                    onCreateTask={(title) => createTask(title, { status: 'in-progress' })}
                                    onToggle={toggleDone}
                                    onDelete={deleteTask}
                                    onUpdate={updateTask}
                                    onDemote={(id) => setDemoted(id, true)}
                                    isDragging={!!activeId}
                                />
                            </div>
                            <div className="w-[85vw] max-w-[85vw] sm:w-auto sm:max-w-none sm:min-w-0 h-full min-h-0 snap-center flex-shrink-0 flex flex-col">
                                <KanbanColumn
                                    id="done"
                                    title="Done"
                                    icon={CheckCircle2}
                                    accentColor="green"
                                    tasks={visibleTasks.filter(t => t.status === 'done')}
                                    onCreateTask={(title) => createTask(title, { status: 'done' })}
                                    onToggle={toggleDone}
                                    onDelete={deleteTask}
                                    onUpdate={updateTask}
                                    onDemote={(id) => setDemoted(id, true)}
                                    isDragging={!!activeId}
                                />
                            </div>
                        </div>
                    )}

                    {activeView === 'matrix' && (
                        <div className="flex min-h-0 flex-1 flex-col px-3 pb-4 md:px-0 md:pb-0">
                            {/* The axes, sitting on the canvas rather than in a
                                surface. Each quadrant used to restate them in a
                                subtitle — four repetitions of the same two words
                                — which is a labelled 2x2 grid, not a matrix. Said
                                once on the edges, the grid position finally
                                carries the meaning. Aligned by using the same
                                gap-3 as the grid below, so labels sit exactly
                                over their tracks. */}
                            <div className="hidden gap-3 pb-1.5 pl-6 sm:grid sm:grid-cols-2">
                                <AxisLabel>Urgent</AxisLabel>
                                <AxisLabel>Not urgent</AxisLabel>
                            </div>
                            <div className="flex min-h-0 flex-1">
                                <div className="hidden w-6 grid-rows-2 gap-3 pr-1.5 sm:grid">
                                    <AxisLabel vertical>Important</AxisLabel>
                                    <AxisLabel vertical>Not important</AxisLabel>
                                </div>
                                <div className="flex min-h-0 flex-1 snap-x snap-mandatory gap-3 overflow-x-auto no-scrollbar touch-pan-x sm:grid sm:grid-cols-2 sm:grid-rows-2 sm:overflow-x-visible">
                                    <div className="w-[85vw] max-w-[85vw] sm:w-auto sm:max-w-none sm:min-w-0 h-full min-h-0 snap-center flex-shrink-0 flex flex-col">
                                        <MatrixQuadrant
                                            id="q1"
                                            title="Do First"
                                            subtitle="Urgent & Important"
                                            color="red"
                                            tasks={visibleTasks.filter(t => t.quadrant === 'q1' && t.status !== 'done')}
                                            onCreateTask={(title) => createTask(title, { quadrant: 'q1', priority: 'high', importance: 'high' })}
                                            onToggle={toggleDone}
                                            onDelete={deleteTask}
                                            onUpdate={updateTask}
                                            onDemote={(id) => setDemoted(id, true)}
                                            isDragging={!!activeId}
                                        />
                                    </div>
                                    <div className="w-[85vw] max-w-[85vw] sm:w-auto sm:max-w-none sm:min-w-0 h-full min-h-0 snap-center flex-shrink-0 flex flex-col">
                                        <MatrixQuadrant
                                            id="q2"
                                            title="Schedule"
                                            subtitle="Important, Not Urgent"
                                            color="blue"
                                            tasks={visibleTasks.filter(t => t.quadrant === 'q2' && t.status !== 'done')}
                                            onCreateTask={(title) => createTask(title, { quadrant: 'q2', priority: 'low', importance: 'high' })}
                                            onToggle={toggleDone}
                                            onDelete={deleteTask}
                                            onUpdate={updateTask}
                                            onDemote={(id) => setDemoted(id, true)}
                                            isDragging={!!activeId}
                                        />
                                    </div>
                                    <div className="w-[85vw] max-w-[85vw] sm:w-auto sm:max-w-none sm:min-w-0 h-full min-h-0 snap-center flex-shrink-0 flex flex-col">
                                        <MatrixQuadrant
                                            id="q3"
                                            title="Delegate"
                                            subtitle="Urgent, Not Important"
                                            color="amber"
                                            tasks={visibleTasks.filter(t => t.quadrant === 'q3' && t.status !== 'done')}
                                            onCreateTask={(title) => createTask(title, { quadrant: 'q3', priority: 'high', importance: 'low' })}
                                            onToggle={toggleDone}
                                            onDelete={deleteTask}
                                            onUpdate={updateTask}
                                            onDemote={(id) => setDemoted(id, true)}
                                            isDragging={!!activeId}
                                        />
                                    </div>
                                    <div className="w-[85vw] max-w-[85vw] sm:w-auto sm:max-w-none sm:min-w-0 h-full min-h-0 snap-center flex-shrink-0 flex flex-col">
                                        <MatrixQuadrant
                                            id="q4"
                                            title="Eliminate"
                                            subtitle="Neither Urgent nor Important"
                                            color="gray"
                                            tasks={visibleTasks.filter(t => t.quadrant === 'q4' && t.status !== 'done')}
                                            onCreateTask={(title) => createTask(title, { quadrant: 'q4', priority: 'low', importance: 'low' })}
                                            onToggle={toggleDone}
                                            onDelete={deleteTask}
                                            onUpdate={updateTask}
                                            onDemote={(id) => setDemoted(id, true)}
                                            isDragging={!!activeId}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeView === 'list' && (
                        <ListView
                            tasks={visibleTasks.filter(t => t.status !== 'done')}
                            onToggle={toggleDone}
                            onDelete={deleteTask}
                            onUpdate={updateTask}
                            onCreateTask={createTask}
                            onDemote={(id) => setDemoted(id, true)}
                        />
                    )}

                    {activeView === 'backlog' && (
                        /* Done work is NOT filtered out here, unlike the list
                           above. A parked task you finished anyway promotes
                           itself on completion, so anything still showing as
                           done in here was demoted after the fact and would
                           otherwise be invisible in every view in the app. */
                        <ListView
                            mode="backlog"
                            tasks={visibleTasks}
                            candidates={activeTasks}
                            onToggle={completeFromBacklog}
                            onDelete={deleteTask}
                            onUpdate={updateTask}
                            onCreateTask={(title) => createTask(title, { demoted: true })}
                            onPromote={(id) => setDemoted(id, false)}
                            onDemoteMany={(ids) => setDemotedMany(ids, true)}
                        />
                    )}
                </div>
            </div>

            {/* Portalled to <body>: the surface clips its content, so an
                in-place overlay would be cut off the moment you drag a card
                toward the surface edge. */}
            {createPortal(
                <DragOverlay>
                    {activeTask ? <TaskCard task={activeTask} isOverlay /> : null}
                </DragOverlay>,
                document.body
            )}
        </DndContext>
    );
}

/**
 * A matrix axis caption. Lives on the ambient canvas, not inside a surface —
 * the empty space between the quadrants is exactly where axis information
 * belongs, and this is the one place in the app where the gap says something.
 */
function AxisLabel({ children, vertical }) {
    return (
        <div
            className={cn(
                'flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/70',
                // rotate(180deg) on top of vertical-rl gives bottom-to-top,
                // which is the reading direction for a y-axis.
                vertical && '[writing-mode:vertical-rl] [transform:rotate(180deg)]'
            )}
        >
            {children}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// KANBAN COLUMN
// ═══════════════════════════════════════════════════════════════════════════════

function KanbanColumn({ id, title, icon: Icon, accentColor = 'primary', tasks, onCreateTask, onToggle, onDelete, onUpdate, onDemote, isDragging }) {
    const { setNodeRef, isOver } = useDroppable({ id });
    const { readOnly } = useProjectContext();
    const [quickAdd, setQuickAdd] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const inputRef = useRef(null);

    const handleQuickAdd = (e) => {
        e.preventDefault();
        if (quickAdd.trim()) {
            onCreateTask(quickAdd);
            setQuickAdd('');
        }
        setIsAdding(false);
    };

    const colorClasses = {
        primary: 'text-primary border-primary/30',
        accent: 'text-accent border-accent/30',
        green: 'text-green-500 border-green-500/30'
    };

    const dropZoneColors = {
        primary: 'border-primary bg-primary/10',
        accent: 'border-accent bg-accent/10',
        green: 'border-green-500 bg-green-500/10'
    };

    const overTint = {
        primary: 'bg-primary/10',
        accent: 'bg-accent/10',
        green: 'bg-green-500/10'
    };

    return (
        <Surface
            ref={setNodeRef}
            variant="tile"
            // Layout, clipping, radius and the resting border all come from the
            // tile skin; only the drag states are stated here.
            className={cn(
                'h-full transition-all duration-200',
                isOver && 'border-primary ring-2 ring-primary/30 scale-[1.02]',
                !isOver && isDragging && 'border-dashed border-primary/30'
            )}
        >
            {/* The drop-target wash rides its own layer rather than going in the
                className above: a `bg-*` there is in the same twMerge group as
                the tile's material and would replace it outright, so the column
                would lose its background mid-drag and show raw canvas. Sits
                below the content, which is `relative`. */}
            <div
                aria-hidden
                className={cn(
                    'pointer-events-none absolute inset-0 transition-colors duration-200',
                    isOver && overTint[accentColor]
                )}
            />

            {/* Header */}
            <div className="relative flex-shrink-0 p-4 border-b border-white/5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${colorClasses[accentColor].split(' ')[0]}`} />
                        <span className="font-semibold text-sm">{title}</span>
                        <span className="text-xs text-muted-foreground bg-white/5 px-2 py-0.5 rounded-full">{tasks.length}</span>
                    </div>
                    {!readOnly && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-lg hover:bg-white/10"
                            onClick={() => { setIsAdding(true); setTimeout(() => inputRef.current?.focus(), 0); }}
                        >
                            <Plus className="h-4 w-4" />
                        </Button>
                    )}
                </div>

                {/* Quick Add */}
                {isAdding && (
                    <form onSubmit={handleQuickAdd} className="mt-3">
                        <Input
                            ref={inputRef}
                            value={quickAdd}
                            onChange={(e) => setQuickAdd(e.target.value)}
                            placeholder="Task name..."
                            className="h-9 text-sm bg-background/50 border-white/10 rounded-xl"
                            onBlur={() => { if (!quickAdd.trim()) setIsAdding(false); }}
                            onKeyDown={(e) => { if (e.key === 'Escape') setIsAdding(false); }}
                        />
                    </form>
                )}
            </div>

            {/* Tasks */}
            <div className="relative flex-1 min-h-0 overflow-y-auto p-3">
                <div className="space-y-2">
                    {/* Drop Zone Indicator - appears at TOP when dragging */}
                    {isDragging ? (
                        <div
                            className={`flex items-center justify-center py-6 px-4 rounded-xl border-2 border-dashed transition-all duration-300 ${isOver
                                ? `${dropZoneColors[accentColor]} scale-105`
                                : 'border-white/20 bg-white/5'
                                }`}
                        >
                            <div className={`flex items-center gap-2 text-sm font-medium transition-colors duration-200 ${isOver ? colorClasses[accentColor].split(' ')[0] : 'text-muted-foreground'
                                }`}>
                                <Plus className={`h-4 w-4 transition-transform duration-200 ${isOver ? 'scale-125' : ''}`} />
                                <span>{isOver ? 'Drop here' : 'Drop to move here'}</span>
                            </div>
                        </div>
                    ) : (
                        /* Always visible inline add at TOP - hide when dragging or readOnly */
                        !readOnly && (
                            <InlineTaskAdd
                                onAdd={onCreateTask}
                                placeholder={tasks.length === 0 ? 'Add your first task...' : 'Add another task...'}
                            />
                        )
                    )}

                    {tasks.map(task => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            onToggle={() => onToggle(task)}
                            onDelete={() => onDelete(task.id)}
                            onUpdate={onUpdate}
                            onDemote={() => onDemote(task.id)}
                        />
                    ))}
                </div>
            </div>
        </Surface>
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MATRIX QUADRANT
// ═══════════════════════════════════════════════════════════════════════════════

function MatrixQuadrant({ id, title, subtitle, color, tasks, onCreateTask, onToggle, onDelete, onUpdate, onDemote, isDragging }) {
    const { setNodeRef, isOver } = useDroppable({ id });
    const { readOnly } = useProjectContext();
    const [quickAdd, setQuickAdd] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const inputRef = useRef(null);

    const handleQuickAdd = (e) => {
        e.preventDefault();
        if (quickAdd.trim()) {
            onCreateTask(quickAdd);
            setQuickAdd('');
        }
        setIsAdding(false);
    };

    // Borders and rings only — the fills live on the tint layer below, because a
    // `bg-*` in the Surface className replaces the tile material instead of
    // sitting on top of it (same twMerge group).
    const colorStyles = {
        red: 'border-red-500/30',
        blue: 'border-blue-500/30',
        amber: 'border-amber-500/30',
        gray: 'border-white/10'
    };

    const colorStylesActive = {
        red: 'border-red-500 ring-2 ring-red-500/30',
        blue: 'border-blue-500 ring-2 ring-blue-500/30',
        amber: 'border-amber-500 ring-2 ring-amber-500/30',
        gray: 'border-white/50 ring-2 ring-white/20'
    };

    // Resting wash: the only thing that tells the four quadrants apart at a
    // glance, so it has to beat whatever the project's ambient canvas is doing
    // behind it. An orange-tinted project pulled red and amber together at /0.07.
    const tintStyles = {
        red: 'bg-red-500/[0.10]',
        blue: 'bg-blue-500/[0.10]',
        amber: 'bg-amber-500/[0.10]',
        gray: 'bg-white/[0.03]'
    };

    const tintStylesActive = {
        red: 'bg-red-500/20',
        blue: 'bg-blue-500/20',
        amber: 'bg-amber-500/20',
        gray: 'bg-white/10'
    };

    const colorStylesDragging = {
        red: 'border-red-500/50 border-dashed',
        blue: 'border-blue-500/50 border-dashed',
        amber: 'border-amber-500/50 border-dashed',
        gray: 'border-white/30 border-dashed'
    };

    const dropZoneColors = {
        red: 'border-red-500 bg-red-500/10 text-red-400',
        blue: 'border-blue-500 bg-blue-500/10 text-blue-400',
        amber: 'border-amber-500 bg-amber-500/10 text-amber-400',
        gray: 'border-white/40 bg-white/10 text-white/70'
    };

    const headerColors = {
        red: 'text-red-400',
        blue: 'text-blue-400',
        amber: 'text-amber-400',
        gray: 'text-muted-foreground'
    };

    return (
        <Surface
            ref={setNodeRef}
            variant="tile"
            className={cn(
                'h-full transition-all duration-200',
                isOver
                    ? `${colorStylesActive[color]} scale-[1.02]`
                    : isDragging
                        ? colorStylesDragging[color]
                        : colorStyles[color]
            )}
        >
            <div
                aria-hidden
                className={cn(
                    'pointer-events-none absolute inset-0 transition-colors duration-200',
                    isOver ? tintStylesActive[color] : tintStyles[color]
                )}
            />

            {/* Header */}
            <div className="relative flex-shrink-0 p-4 border-b border-white/5">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className={`font-bold text-sm uppercase tracking-wide ${headerColors[color]}`}>{title}</h3>
                        {/* Desktop states this on the axes instead — see AxisLabel.
                            Mobile has no axes (the matrix is a carousel there), so
                            the subtitle is still the only thing explaining the
                            quadrant and has to stay. */}
                        <p className="text-[10px] text-muted-foreground sm:hidden">{subtitle}</p>
                    </div>

                    {!readOnly && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-lg hover:bg-white/10"
                            onClick={() => { setIsAdding(true); setTimeout(() => inputRef.current?.focus(), 0); }}
                        >
                            <Plus className="h-4 w-4" />
                        </Button>
                    )}
                </div>

                {isAdding && (
                    <form onSubmit={handleQuickAdd} className="mt-3">
                        <Input
                            ref={inputRef}
                            value={quickAdd}
                            onChange={(e) => setQuickAdd(e.target.value)}
                            placeholder="Task name..."
                            className="h-9 text-sm bg-background/50 border-white/10 rounded-xl"
                            onBlur={() => { if (!quickAdd.trim()) setIsAdding(false); }}
                            onKeyDown={(e) => { if (e.key === 'Escape') setIsAdding(false); }}
                        />
                    </form>
                )}
            </div>

            {/* Tasks */}
            <div className="relative flex-1 min-h-0 overflow-y-auto p-3">
                <div className="space-y-2">
                    {/* Drop Zone Indicator - appears at TOP when dragging */}
                    {isDragging ? (
                        <div
                            className={`flex items-center justify-center py-4 px-3 rounded-xl border-2 border-dashed transition-all duration-300 ${isOver
                                ? `${dropZoneColors[color]} scale-105`
                                : 'border-white/20 bg-white/5 text-muted-foreground'
                                }`}
                        >
                            <div className={`flex items-center gap-2 text-xs font-medium transition-all duration-200`}>
                                <Plus className={`h-3 w-3 transition-transform duration-200 ${isOver ? 'scale-125' : ''}`} />
                                <span>{isOver ? 'Drop here' : 'Drop to move'}</span>
                            </div>
                        </div>
                    ) : (
                        /* Always visible inline add at TOP - hide when dragging */
                        !readOnly && (
                            <InlineTaskAdd
                                onAdd={onCreateTask}
                                placeholder="Add task..."
                                compact
                            />
                        )
                    )}

                    {tasks.map(task => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            onToggle={() => onToggle(task)}
                            onDelete={() => onDelete(task.id)}
                            onUpdate={onUpdate}
                            onDemote={() => onDemote(task.id)}
                            compact
                        />
                    ))}
                </div>
            </div>
        </Surface>
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════════════════════════


/**
 * The list, in two modes.
 *
 * `backlog` is the same surface reading the demoted pool: the status column
 * becomes how long the task has been parked, promote joins delete in the action
 * cell, and the quick-add creates tasks that are born parked. Parameterised
 * rather than forked because everything that makes a row readable — the badges,
 * the notes editor, the inline rename — is shared, and a second copy of it is
 * how the two drift apart on the next change to any of them.
 */
function ListView({ mode = 'active', tasks, candidates = [], onToggle, onDelete, onUpdate, onCreateTask, onDemote, onPromote, onDemoteMany }) {
    const { readOnly } = useProjectContext();
    const [quickAdd, setQuickAdd] = useState('');
    const [picking, setPicking] = useState(false);
    const inBacklog = mode === 'backlog';
    // Most-recently-parked first in the backlog: you should see what you just
    // put there. "Longest parked" is one click away and is the sort that
    // actually surfaces rot.
    const [sortBy, setSortBy] = useState(inBacklog ? 'parked-new' : 'newest');

    const handleQuickAdd = (e) => {
        e.preventDefault();
        if (quickAdd.trim()) {
            onCreateTask(quickAdd);
            setQuickAdd('');
        }
    };

    const getSortedTasks = () => {
        const sorted = [...tasks];
        const levels = { high: 3, medium: 2, low: 1 };
        const parkedAt = (t) => new Date(t.demoted_at || 0);

        switch (sortBy) {
            case 'priority':
                return sorted.sort((a, b) => (levels[b.priority] || 0) - (levels[a.priority] || 0));
            case 'importance':
                return sorted.sort((a, b) => (levels[b.importance] || 0) - (levels[a.importance] || 0));
            case 'difficulty':
                return sorted.sort((a, b) => (levels[b.difficulty] || 0) - (levels[a.difficulty] || 0));
            case 'parked-old':
                return sorted.sort((a, b) => parkedAt(a) - parkedAt(b));
            case 'parked-new':
                return sorted.sort((a, b) => parkedAt(b) - parkedAt(a));
            case 'newest':
            default:
                // Assuming new tasks are at top (prepended), so index order or created_at?
                // created_at string comparison is usually fine for ISO strings
                return sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }
    };

    const sortedTasks = getSortedTasks();

    const SortButton = ({ id, label }) => (
        <button
            onClick={() => setSortBy(id)}
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg transition-all ${sortBy === id
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                }`}
        >
            {label}
        </button>
    );

    // The list is a single pane rather than a set of tiles, so it takes the
    // full-weight `primary` skin — same material as the Chat and Files
    // surfaces — instead of the lighter tile the board columns use.
    return (
        <Surface className="flex-1">
            {/* Quick Add. In the backlog this creates a task that is born
                parked — a capture box for someday-ideas that should never touch
                the board in the first place.

                Both buttons name the *source* of the task rather than the verb,
                because in here the verb is the same for both and "Add" / "Add
                existing" left the pair reading as one button and its footnote:
                new idea vs. something already on the board. */}
            {!readOnly && (
                <form onSubmit={handleQuickAdd} className="flex-shrink-0 p-4 border-b border-white/5">
                    <div className="flex gap-2">
                        <Input
                            value={quickAdd}
                            onChange={(e) => setQuickAdd(e.target.value)}
                            placeholder={inBacklog ? 'Park an idea for later...' : 'Add a new task...'}
                            className="flex-1 h-10 bg-background/50 border-white/10 rounded-xl"
                        />
                        <Button type="submit" className="h-10 px-4 rounded-xl" disabled={!quickAdd.trim()}>
                            <Plus className={inBacklog ? 'h-4 w-4 sm:mr-2' : 'h-4 w-4 mr-2'} />
                            {inBacklog ? <span className="hidden sm:inline">Create demoted</span> : 'Add'}
                        </Button>
                        {inBacklog && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setPicking(true)}
                                className="h-10 px-4 rounded-xl border-white/10"
                            >
                                <ChevronsDown className="h-4 w-4 sm:mr-2" />
                                <span className="hidden sm:inline">Demote existing</span>
                            </Button>
                        )}
                    </div>
                </form>
            )}

            {/* Header - with Sort Controls */}
            <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase">
                    <span className="ml-1">{inBacklog ? 'Backlog' : 'Tasks'}</span>
                    <span className="bg-white/5 px-1.5 py-0.5 rounded text-[10px]">{tasks.length}</span>
                </div>

                <div className="flex items-center gap-1">
                    <ArrowUpDown className="h-3 w-3 text-muted-foreground mr-1" />
                    {inBacklog ? (
                        <>
                            <SortButton id="parked-new" label="Recent" />
                            <SortButton id="parked-old" label="Longest" />
                        </>
                    ) : (
                        <SortButton id="newest" label="Newest" />
                    )}
                    <SortButton id="priority" label="Priority" />
                    <SortButton id="importance" label="Importance" />
                    <SortButton id="difficulty" label="Difficulty" />
                </div>
            </div>

            {/* List */}
            <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="divide-y divide-white/5">
                    {sortedTasks.map(task => (
                        <ListItem
                            key={task.id}
                            mode={mode}
                            task={task}
                            onToggle={() => onToggle(task)}
                            onDelete={() => onDelete(task.id)}
                            onUpdate={onUpdate}
                            onDemote={onDemote ? () => onDemote(task.id) : undefined}
                            onPromote={onPromote ? () => onPromote(task.id) : undefined}
                        />
                    ))}
                    {tasks.length === 0 && (
                        <div className="py-12 text-center text-muted-foreground">
                            {inBacklog ? (
                                <>
                                    <Archive className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Nothing in the backlog</p>
                                    <p className="text-xs">Demote a task to park it here for later</p>
                                </>
                            ) : (
                                <>
                                    <ListTodo className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">No tasks yet</p>
                                    <p className="text-xs">Add one above to get started</p>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {inBacklog && (
                <DemoteTasksDialog
                    open={picking}
                    onOpenChange={setPicking}
                    tasks={candidates}
                    onConfirm={onDemoteMany}
                />
            )}
        </Surface>
    );
}

function ListItem({ mode = 'active', task, onToggle, onDelete, onUpdate, onDemote, onPromote }) {
    const inBacklog = mode === 'backlog';
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(task.title);

    const handleSave = () => {
        if (editValue.trim() && editValue !== task.title) {
            onUpdate(task.id, { title: editValue });
        }
        setIsEditing(false);
    };

    const cyclePriority = (e) => {
        e?.stopPropagation();
        const cycle = { low: 'medium', medium: 'high', high: 'low' };
        onUpdate(task.id, { priority: cycle[task.priority] || 'medium' });
    };

    const cycleImportance = (e) => {
        e?.stopPropagation();
        const cycle = { low: 'medium', medium: 'high', high: 'low' };
        onUpdate(task.id, { importance: cycle[task.importance] || 'medium' });
    };

    const cycleDifficulty = (e) => {
        e?.stopPropagation();
        const cycle = { low: 'medium', medium: 'high', high: 'low' };
        onUpdate(task.id, { difficulty: cycle[task.difficulty] || 'medium' });
    };

    return (
        <div className={`hover:bg-white/5 transition-colors group ${task.status === 'done' ? 'opacity-50' : ''}`}>
            <div className="flex sm:grid sm:grid-cols-12 px-4 py-3 items-start gap-3 sm:gap-0">
                {/* Checkbox. In the backlog it does two things at once — marks
                    it done AND promotes it — because the alternative is finished
                    work parked where no Done column, no count and no activity
                    feed will ever show it. That is defensible; leaving it to be
                    guessed from a bare circle was not, which is what the title
                    below is for. Reopening is deliberately not symmetric, so it
                    says so too. */}
                <div className="sm:col-span-1 pt-0.5 flex-shrink-0">
                    <button
                        onClick={onToggle}
                        className="p-1"
                        title={
                            task.status === 'done'
                                ? (inBacklog ? 'Reopen — stays in the backlog' : 'Reopen')
                                : (inBacklog ? 'Mark done — promotes it back to the board' : 'Mark done')
                        }
                        aria-label={task.status === 'done' ? 'Reopen task' : 'Mark task done'}
                    >
                        {task.status === 'done'
                            ? <CheckCircle2 className="h-5 w-5 text-green-500" />
                            : <div className="h-5 w-5 rounded-full border-2 border-muted-foreground hover:border-primary transition-colors" />
                        }
                    </button>
                </div>

                {/* Main Content (Title + Chips + Notes).
                    7 columns, not 8/9: with the old spans the four cells summed
                    to 13 of 12 tracks at md and up, so the action cell wrapped
                    onto a second row underneath the checkbox. That was already
                    true with one button in it — a second one just made it
                    impossible to keep ignoring. 1 + 7 + 2 + 2 = 12 at every
                    breakpoint. */}
                <div className="flex-1 sm:col-span-7 pr-4 min-w-0">
                    {isEditing ? (
                        <div>
                            <SmartInput
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={handleSave}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setIsEditing(false); }}
                                className="h-8 text-sm bg-transparent border-white/10 w-full"
                                autoFocus
                            />
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                <button onClick={cyclePriority} title="Priority"><PriorityBadge priority={task.priority} label="P" clickable /></button>
                                <button onClick={cycleImportance} title="Importance"><ImportanceBadge importance={task.importance} clickable /></button>
                                <button onClick={cycleDifficulty} title="Difficulty"><DifficultyBadge difficulty={task.difficulty} clickable /></button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div
                                onClick={() => setIsEditing(true)}
                                className={`text-left text-sm font-medium w-full cursor-text ${task.status === 'done' ? 'line-through' : ''}`}
                            >
                                <div className="break-words mb-1">
                                    <SmartText text={task.title} className="inline" />
                                </div>

                                {/* Inline Tags */}
                                <div className="flex flex-wrap items-center gap-1.5 align-middle">
                                    <button onClick={cyclePriority} title="Priority" className="inline-flex"><PriorityBadge priority={task.priority} label="P" clickable /></button>
                                    <button onClick={cycleImportance} title="Importance" className="inline-flex"><ImportanceBadge importance={task.importance} clickable /></button>
                                    <button onClick={cycleDifficulty} title="Difficulty" className="inline-flex"><DifficultyBadge difficulty={task.difficulty} clickable /></button>
                                    <DueBadge task={task} onUpdate={onUpdate} muted={inBacklog} />

                                    {/* Status Badge (Desktop inline-ish/Mobile fallback) */}
                                    <span className={`sm:hidden text-[9px] font-medium uppercase px-1.5 py-0.5 rounded bg-white/5 text-muted-foreground`}>
                                        {inBacklog ? `parked ${formatElapsed(task.demoted_at)}` : task.status.replace('-', ' ')}
                                    </span>
                                </div>
                            </div>

                            {/* Notes in List View */}
                            <div className="pl-0">
                                <TaskNotes task={task} onUpdate={onUpdate} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Desktop Status Column. In the backlog the status is beside
                    the point — everything here is deferred — so the column says
                    how long it has been sitting instead, which is the only
                    number that should make you reconsider. */}
                <div className="hidden sm:block sm:col-span-2 pt-1.5">
                    {inBacklog ? (
                        <span className="text-[10px] font-medium uppercase px-2 py-1 rounded-lg bg-white/5 text-muted-foreground">
                            parked {formatElapsed(task.demoted_at)}
                        </span>
                    ) : (
                        <span className={`text-[10px] font-medium uppercase px-2 py-1 rounded-lg ${task.status === 'todo' ? 'bg-muted text-muted-foreground' :
                            task.status === 'in-progress' ? 'bg-accent/20 text-accent' :
                                'bg-green-500/20 text-green-500'
                            }`}>
                            {task.status.replace('-', ' ')}
                        </span>
                    )}
                </div>

                {/* Actions */}
                <div className="sm:col-span-2 flex items-start justify-end pt-0.5 flex-shrink-0">
                    {onPromote && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:bg-primary/20 hover:text-primary transition-all"
                            onClick={onPromote}
                            title="Promote to board"
                            aria-label="Promote to board"
                        >
                            <ChevronsUp className="h-4 w-4" />
                        </Button>
                    )}
                    {onDemote && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all"
                            onClick={onDemote}
                            title="Demote to backlog"
                            aria-label="Demote to backlog"
                        >
                            <ChevronsDown className="h-4 w-4" />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 sm:group-hover:opacity-100 hover:bg-red-500/20 hover:text-red-500 transition-all"
                        onClick={() => onDelete(task.id)}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TASK CARD
// ═══════════════════════════════════════════════════════════════════════════════

function TaskCard({ task, onToggle, onDelete, onUpdate, onDemote, isOverlay, compact }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
    const { readOnly } = useProjectContext();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(task.title);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const handleSave = () => {
        if (editValue.trim() && editValue !== task.title) {
            onUpdate?.(task.id, { title: editValue });
        }
        setIsEditing(false);
    };

    const cyclePriority = (e) => {
        e.stopPropagation();
        const cycle = { low: 'medium', medium: 'high', high: 'low' };
        onUpdate?.(task.id, { priority: cycle[task.priority] || 'medium' });
    };

    const cycleImportance = (e) => {
        e.stopPropagation();
        const cycle = { low: 'medium', medium: 'high', high: 'low' };
        onUpdate?.(task.id, { importance: cycle[task.importance] || 'medium' });
    };

    const cycleDifficulty = (e) => {
        e.stopPropagation();
        const cycle = { low: 'medium', medium: 'high', high: 'low' };
        onUpdate?.(task.id, { difficulty: cycle[task.difficulty] || 'medium' });
    };

    return (
        <Card
            ref={setNodeRef}
            style={style}
            className={`
        border-white/10 hover:border-primary/30 transition-all cursor-grab active:cursor-grabbing group
        ${task.status === 'done' ? 'opacity-50 bg-secondary/20' : 'bg-secondary/40'}
        ${isDragging ? 'opacity-50 scale-105' : ''}
        ${isOverlay ? 'shadow-2xl shadow-black/50 rotate-2' : ''}
      `}
        >
            <CardContent className={compact ? 'p-2' : 'p-3'}>
                <div className="flex items-start gap-2">
                    {/* Drag Handle */}
                    {!readOnly && (
                        <div
                            {...attributes}
                            {...listeners}
                            className="mt-0.5 flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 -ml-1 rounded hover:bg-white/10 transition-colors touch-none"
                            title="Drag to move"
                        >
                            <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                        </div>
                    )}

                    {/* Checkbox */}
                    {onToggle && (
                        <button
                            className={`mt-0.5 flex-shrink-0 ${readOnly ? 'cursor-default' : ''}`}
                            onClick={(e) => { e.stopPropagation(); if (!readOnly) onToggle(); }}
                        >
                            {task.status === 'done'
                                ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                                : <div className="h-4 w-4 rounded-full border-2 border-muted-foreground hover:border-green-500 hover:bg-green-500/20 transition-all" />
                            }
                        </button>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                        {isEditing ? (
                            <div>
                                <SmartInput
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={handleSave}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSave();
                                        if (e.key === 'Escape') { setEditValue(task.title); setIsEditing(false); }
                                    }}
                                    className="h-6 text-sm p-0 bg-transparent border-0 border-b border-primary/50 rounded-none focus-visible:ring-0 w-full"
                                    autoFocus
                                />
                                <div className="flex gap-1.5 mt-1.5">
                                    <button onClick={cyclePriority} onPointerDown={(e) => e.stopPropagation()} title="Priority">
                                        <PriorityBadge priority={task.priority} label="P" clickable />
                                    </button>
                                    <button onClick={cycleImportance} onPointerDown={(e) => e.stopPropagation()} title="Importance">
                                        <ImportanceBadge importance={task.importance} clickable />
                                    </button>
                                    <button onClick={cycleDifficulty} onPointerDown={(e) => e.stopPropagation()} title="Difficulty">
                                        <DifficultyBadge difficulty={task.difficulty} clickable />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div
                                onClick={() => !readOnly && onUpdate && setIsEditing(true)}
                                onPointerDown={(e) => e.stopPropagation()}
                                className={`text-left text-sm font-medium w-full ${!readOnly ? 'cursor-text' : ''} ${task.status === 'done' ? 'line-through text-muted-foreground' : ''}`}
                            >
                                <div className="break-words mb-1.5">
                                    <SmartText text={task.title} className="inline" />
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    <button
                                        onClick={cyclePriority}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        title="Click to change priority"
                                        className="inline-flex"
                                    >
                                        <PriorityBadge priority={task.priority} label="P" clickable />
                                    </button>
                                    <button
                                        onClick={cycleImportance}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        title="Click to change importance"
                                        className="inline-flex"
                                    >
                                        <ImportanceBadge importance={task.importance} clickable />
                                    </button>
                                    <button
                                        onClick={cycleDifficulty}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        title="Click to change difficulty"
                                        className="inline-flex"
                                    >
                                        <DifficultyBadge difficulty={task.difficulty} clickable />
                                    </button>
                                    <DueBadge task={task} onUpdate={onUpdate} />
                                </div>
                            </div>
                        )}

                        {/* Notes Section */}
                        <TaskNotes
                            task={task}
                            onUpdate={onUpdate}
                            isOverlay={isOverlay}
                        />

                        {!compact && (
                            /* Quick Priority & Importance Toggles - Only show in full view if needed, or keep them? 
                               User asked for "notes and smart references". 
                               The code I see in line 733 in previous view showed TaskNotes inside !compact.
                               Let's just remove the condition.
                            */
                            null
                        )}
                    </div>

                    {/* Demote + Delete. Both stop pointerdown before it reaches
                        the card, or the drag sensor claims the gesture and the
                        click never lands. */}
                    <div className="flex flex-shrink-0 items-start">
                        {onDemote && !readOnly && !isOverlay && (
                            <button
                                className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-white/10 text-muted-foreground hover:text-foreground transition-all"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); onDemote(); }}
                                title="Demote to backlog"
                                aria-label="Demote to backlog"
                            >
                                <ChevronsDown className="h-4 w-4" />
                            </button>
                        )}

                        {onDelete && !readOnly && (
                            <button
                                className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 bg-red-500/0 hover:bg-red-500/20 text-muted-foreground hover:text-red-500 transition-all"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                title="Delete task"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function TaskNotes({ task, onUpdate, isOverlay }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [noteValue, setNoteValue] = useState(task.description || '');
    const [showFullNotes, setShowFullNotes] = useState(false);

    const hasNotes = !!task.description;
    const MAX_LENGTH = 120;
    const isLong = task.description && task.description.length > MAX_LENGTH;

    const handleSave = () => {
        if (noteValue !== task.description) {
            onUpdate?.(task.id, { description: noteValue });
        }
        setIsEditing(false);
    };

    if (isOverlay) return null; // Don't show complex notes in drag overlay

    return (
        <div className="mt-2" onPointerDown={(e) => e.stopPropagation()}>
            {!isExpanded && !hasNotes ? (
                <button
                    onClick={() => { setIsExpanded(true); setIsEditing(true); }}
                    className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
                >
                    <Plus className="h-3 w-3" /> Add notes
                </button>
            ) : (
                <div className="text-xs">
                    {!isEditing ? (
                        <div className="group/notes cursor-pointer">
                            <div
                                onClick={() => setIsEditing(true)}
                                className="text-muted-foreground/80 hover:text-foreground transition-colors inline"
                            >
                                <SmartText
                                    text={!showFullNotes && isLong ? task.description.slice(0, MAX_LENGTH) + '...' : task.description}
                                    className="inline"
                                />
                                {isLong && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setShowFullNotes(!showFullNotes); }}
                                        className="text-[10px] text-primary hover:underline ml-1 inline font-medium align-baseline"
                                    >
                                        {showFullNotes ? 'Show less' : 'Show more'}
                                    </button>
                                )}
                            </div>
                            {!task.description && <span className="text-muted-foreground italic" onClick={() => setIsEditing(true)}>Empty notes...</span>}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <SmartInput
                                value={noteValue}
                                onChange={(e) => setNoteValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSave();
                                    }
                                    if (e.key === 'Escape') {
                                        setIsEditing(false);
                                        setNoteValue(task.description || '');
                                        if (!task.description) setIsExpanded(false);
                                    }
                                }}
                                className="w-full text-xs bg-black/20 border-white/10 rounded-lg min-h-[60px] p-2 align-top"
                                placeholder="Add notes... (@ to reference files)"
                                autoFocus
                            />
                            <div className="flex justify-end gap-2">
                                <button onClick={() => { setIsEditing(false); setNoteValue(task.description || ''); if (!task.description) setIsExpanded(false); }} className="text-[10px] hover:underline">Cancel</button>
                                <button onClick={handleSave} className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded hover:bg-primary/30">Save</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Always visible inline task add
function InlineTaskAdd({ onAdd, placeholder = 'Add a task...', compact = false }) {
    const [value, setValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (value.trim()) {
            onAdd(value);
            setValue('');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="mt-2 text-left">
            <div className={`flex items-center gap-2 rounded-xl border transition-all ${isFocused ? 'border-primary/50 bg-primary/5' : 'border-dashed border-white/10 hover:border-white/20'
                } ${compact ? 'p-1.5' : 'p-2'}`}>
                <Plus className={`flex-shrink-0 text-muted-foreground ${compact ? 'h-3 w-3' : 'h-4 w-4'}`} />
                <SmartInput
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    className={`flex-1 bg-transparent outline-none placeholder:text-muted-foreground/50 border-none h-auto p-0 focus-visible:ring-0 ${compact ? 'text-xs' : 'text-sm'}`}
                />
                {value.trim() && (
                    <Button type="submit" size="sm" className={`rounded-lg ${compact ? 'h-6 px-2 text-xs' : 'h-7 px-3'}`}>
                        Add
                    </Button>
                )}
            </div>
        </form>
    );
}

// Colours and labels come from components/tasks/taskMeta.js so the board and the
// chat task panel describe a task identically.
function LevelBadge({ meta, value, prefix, clickable }) {
    const level = value || 'medium';
    const config = meta[level] ?? meta.medium;

    return (
        <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border transition-all ${config.chip} ${clickable ? 'cursor-pointer hover:opacity-80 hover:scale-105' : ''}`}>
            {prefix ? `${prefix}:${config.label}` : config.label}
        </span>
    );
}

function PriorityBadge({ priority, label, clickable }) {
    return <LevelBadge meta={PRIORITY_META} value={priority} prefix={label} clickable={clickable} />;
}

function ImportanceBadge({ importance, clickable }) {
    return <LevelBadge meta={IMPORTANCE_META} value={importance} prefix="I" clickable={clickable} />;
}

function DifficultyBadge({ difficulty, clickable }) {
    return <LevelBadge meta={DIFFICULTY_META} value={difficulty} prefix="D" clickable={clickable} />;
}

/**
 * Thin adapter over the shared badge, kept only so the two board call sites can
 * go on passing a whole task.
 *
 * The badge itself moved to components/tasks/DueBadge — the chat panel and the
 * changeset need exactly this control, and a second copy of it there is how
 * "2 days late" ends up meaning two different things in two places. Moving it
 * also took the tooltip through `toLocalDay`: it read `new Date(due_date)`,
 * which on a bare YYYY-MM-DD is UTC midnight and shows the previous day
 * everywhere west of Greenwich.
 */
function DueBadge({ task, onUpdate, muted }) {
    return (
        <SharedDueBadge
            due={task.due_date}
            muted={muted}
            testId={`task-due-${task.id}`}
            onClear={onUpdate ? () => onUpdate(task.id, { due_date: null }) : undefined}
        />
    );
}
